/**
 * Templates de pièces avec packs intelligents de travaux
 * Permet d'ajouter rapidement un ensemble cohérent de lignes de devis
 */

import { CorpsMetier } from '../types/subscription';
import { TypeLigneDevis } from '../types';
import {
  PACKS_MACON_EXTENSION,
  PACKS_MACON_TERRASSE,
  PACKS_MACON_MURS,
  PACKS_MACON_ESCALIERS,
  PACKS_MACON_RENOVATION,
  PACKS_MACON_ETANCHEITE,
} from './packsMacon';

// Type pour une ligne de template
export interface TemplateLigne {
  description: string;
  type: TypeLigneDevis;
  quantite: number;
  prixUnitaire: number;
  tailleReference?: string; // Taille carrelage, diamètre, section câble
  unite?: string; // m², ml, pièce, forfait
  modeSaisieSurface?: 'direct' | 'dimensions';
  avecPertes?: boolean;
}

// Type pour un pack de travaux
export interface PackTravaux {
  id: string;
  nom: string;
  description: string;
  icone: string; // Emoji
  lignes: TemplateLigne[];
  prixEstime: number; // Prix total estimé du pack
}

// Type pour une pièce
export interface PieceTemplate {
  id: string;
  nom: string;
  icone: string; // Emoji
  description: string;
  metiers: CorpsMetier[]; // Métiers compatibles avec cette pièce
  packs: Record<CorpsMetier, PackTravaux[]>; // Packs spécifiques par métier
}

// ============================================================================
// TEMPLATES DE PACKS - ÉLECTRICIEN
// ============================================================================

const PACKS_ELECTRICIEN_CUISINE: PackTravaux[] = [
  {
    id: 'elec_cuisine_standard',
    nom: 'Pack Cuisine Standard',
    description: '6 prises plan de travail + circuits spécialisés + éclairage',
    icone: '⚡',
    prixEstime: 720,
    lignes: [
      {
        description: 'Circuit prises plan de travail (6 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 32,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Circuit four 32A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 110,
        tailleReference: '6mm² (plaques de cuisson)',
        unite: 'circuit',
      },
      {
        description: 'Circuit lave-vaisselle 16A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 75,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Éclairage spots LED encastrables (5 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 5,
        prixUnitaire: 30,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur va-et-vient éclairage',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 22,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'elec_cuisine_premium',
    nom: 'Pack Cuisine Premium',
    description: 'Standard + plaque induction + hotte + variateur',
    icone: '✨',
    prixEstime: 1150,
    lignes: [
      {
        description: 'Circuit prises plan de travail (8 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 8,
        prixUnitaire: 32,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Circuit plaque induction 32A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 150,
        tailleReference: '6mm² (plaques de cuisson)',
        unite: 'circuit',
      },
      {
        description: 'Circuit four encastrable',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 110,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Circuit lave-vaisselle 16A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 75,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Circuit hotte aspirante',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 65,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'circuit',
      },
      {
        description: 'Bandeau LED sous meubles hauts avec variateur',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 165,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'forfait',
      },
      {
        description: 'Spots LED encastrables (8 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 8,
        prixUnitaire: 30,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_SALLE_DE_BAIN: PackTravaux[] = [
  {
    id: 'elec_sdb_standard',
    nom: 'Pack Salle de Bain Standard',
    description: 'Prises IP44 + éclairage IP65 + VMC',
    icone: '💧',
    prixEstime: 580,
    lignes: [
      {
        description: 'Prises IP44 (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 45,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Spots LED IP65 (4 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 38,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur IP44',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 28,
        unite: 'pièce',
      },
      {
        description: 'Circuit VMC salle de bain',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 82,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'circuit',
      },
      {
        description: 'Applique murale LED IP44',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 55,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'elec_sdb_premium',
    nom: 'Pack Salle de Bain Premium',
    description: 'Standard + sèche-serviettes + miroir LED + chauffage sol',
    icone: '🌟',
    prixEstime: 1080,
    lignes: [
      {
        description: 'Prises IP44 (4 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 55,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Circuit sèche-serviettes électrique 1500W',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 180,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Miroir LED avec prise rasoir intégrée',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 120,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'forfait',
      },
      {
        description: 'Spots LED IP65 encastrables (6 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 45,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Circuit VMC hygroréglable',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 120,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'circuit',
      },
      {
        description: 'Interrupteurs IP44 (2 interrupteurs)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 35,
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_CHAMBRE: PackTravaux[] = [
  {
    id: 'elec_chambre_standard',
    nom: 'Pack Chambre Standard',
    description: '5 prises + éclairage + va-et-vient',
    icone: '🛏️',
    prixEstime: 370,
    lignes: [
      {
        description: 'Prises électriques (5 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 5,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Plafonnier LED',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 75,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur va-et-vient (2 points)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 30,
        unite: 'pièce',
      },
      {
        description: 'Prise RJ45 (réseau)',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 45,
        tailleReference: 'RJ45 Cat 6 (réseau)',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_SALON: PackTravaux[] = [
  {
    id: 'elec_salon_standard',
    nom: 'Pack Salon Standard',
    description: '8 prises + éclairage + TV/réseau',
    icone: '🛋️',
    prixEstime: 600,
    lignes: [
      {
        description: 'Prises électriques (8 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 8,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Spots LED encastrables (6 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 35,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises RJ45 + TV/SAT (2 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 65,
        tailleReference: 'RJ45 Cat 6 (réseau)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteurs va-et-vient (2 points)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 30,
        unite: 'pièce',
      },
      {
        description: 'Variateur pour éclairage',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 55,
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_BUREAU: PackTravaux[] = [
  {
    id: 'elec_bureau_standard',
    nom: 'Pack Bureau Standard',
    description: 'Multi-prises + réseau RJ45 + éclairage',
    icone: '💼',
    prixEstime: 580,
    lignes: [
      {
        description: 'Prises électriques bureau (6 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Prises RJ45 réseau (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 45,
        tailleReference: 'RJ45 Cat 6 (réseau)',
        unite: 'pièce',
      },
      {
        description: 'Spots LED bureau (4 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 35,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur avec variateur',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 55,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'elec_bureau_premium',
    nom: 'Pack Bureau Premium Connecté',
    description: 'Standard + réseau + USB intégré + éclairage indirect',
    icone: '🖥️',
    prixEstime: 820,
    lignes: [
      {
        description: 'Prises avec USB intégrées (4 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 65,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Prises classiques (4 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Réseau RJ45 Cat 6A (4 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 55,
        tailleReference: 'RJ45 Cat 6A (réseau)',
        unite: 'pièce',
      },
      {
        description: 'Bandeau LED éclairage indirect avec variateur',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 220,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'forfait',
      },
      {
        description: 'Spots LED encastrables (6 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 35,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_BUANDERIE: PackTravaux[] = [
  {
    id: 'elec_buanderie_standard',
    nom: 'Pack Buanderie Standard',
    description: 'Circuits spécialisés lave-linge + sèche-linge',
    icone: '🧺',
    prixEstime: 380,
    lignes: [
      {
        description: 'Circuit lave-linge 16A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 85,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Circuit sèche-linge 16A',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 85,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Prises électriques supplémentaires (2 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Éclairage néon LED',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 45,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_GARAGE: PackTravaux[] = [
  {
    id: 'elec_garage_standard',
    nom: 'Pack Garage Standard',
    description: 'Éclairage + prises + interrupteur',
    icone: '🚗',
    prixEstime: 420,
    lignes: [
      {
        description: 'Néons LED étanches (3 néons)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 55,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises électriques (4 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur étanche',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 25,
        unite: 'pièce',
      },
      {
        description: 'Prise pour lave-linge/congélateur 16A',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 45,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'elec_garage_motorisation',
    nom: 'Pack Garage avec Motorisation',
    description: 'Standard + porte motorisée + éclairage automatique',
    icone: '🏎️',
    prixEstime: 740,
    lignes: [
      {
        description: 'Circuit motorisation porte de garage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 180,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'circuit',
      },
      {
        description: 'Néons LED avec détecteur de mouvement (3 néons)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 75,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises électriques (5 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 5,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur + télécommande',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 45,
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_EXTERIEUR: PackTravaux[] = [
  {
    id: 'elec_exterieur_jardin',
    nom: 'Pack Éclairage Jardin',
    description: 'Éclairage extérieur + prises étanches',
    icone: '🌳',
    prixEstime: 680,
    lignes: [
      {
        description: 'Appliques murales extérieures LED (4 appliques)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 75,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Spots LED encastrés sol (6 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 6,
        prixUnitaire: 45,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises étanches IP65 (2 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 65,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur crépusculaire automatique',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 85,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'elec_exterieur_terrasse',
    nom: 'Pack Terrasse Éclairée',
    description: 'Éclairage ambiance + prises + détecteur',
    icone: '🏖️',
    prixEstime: 950,
    lignes: [
      {
        description: 'Bandeau LED extérieur RGB avec télécommande',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 320,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'forfait',
      },
      {
        description: 'Spots LED encastrables extérieur (8 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 8,
        prixUnitaire: 45,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises étanches IP65 (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 65,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Détecteur de mouvement extérieur',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 75,
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_ENTREE: PackTravaux[] = [
  {
    id: 'elec_entree_standard',
    nom: 'Pack Entrée Standard',
    description: 'Éclairage + prises + interphone',
    icone: '🚪',
    prixEstime: 480,
    lignes: [
      {
        description: 'Spots LED encastrables (4 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 35,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises électriques (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur va-et-vient (2 points)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 30,
        unite: 'pièce',
      },
      {
        description: 'Installation interphone audio',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 150,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'elec_entree_portail',
    nom: 'Pack Entrée avec Portail Électrique',
    description: 'Standard + motorisation portail + visiophone',
    icone: '🏡',
    prixEstime: 1080,
    lignes: [
      {
        description: 'Circuit motorisation portail coulissant/battant',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 380,
        tailleReference: '2.5mm² (prises)',
        unite: 'circuit',
      },
      {
        description: 'Installation visiophone vidéo connecté',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 280,
        unite: 'forfait',
      },
      {
        description: 'Éclairage entrée avec détecteur (2 appliques)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 85,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Spots LED encastrables (4 spots)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 35,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises électriques (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_ELECTRICIEN_CAVE: PackTravaux[] = [
  {
    id: 'elec_cave_standard',
    nom: 'Pack Cave Standard',
    description: 'Éclairage étanche + prises + VMC',
    icone: '🍷',
    prixEstime: 380,
    lignes: [
      {
        description: 'Néons LED étanches (2 néons)',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 55,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'pièce',
      },
      {
        description: 'Prises électriques (3 prises)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 35,
        tailleReference: '2.5mm² (prises)',
        unite: 'pièce',
      },
      {
        description: 'Interrupteur étanche',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 25,
        unite: 'pièce',
      },
      {
        description: 'Circuit VMC simple flux',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 95,
        tailleReference: '1.5mm² (éclairage)',
        unite: 'circuit',
      },
    ],
  },
];

// ============================================================================
// TEMPLATES DE PACKS - PLOMBIER
// ============================================================================

const PACKS_PLOMBIER_CUISINE: PackTravaux[] = [
  {
    id: 'plomb_cuisine_standard',
    nom: 'Pack Cuisine Standard',
    description: 'Arrivée évier + évacuation + lave-vaisselle',
    icone: '🚰',
    prixEstime: 490,
    lignes: [
      {
        description: 'Installation évier avec mitigeur',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 250,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Arrivée eau chaude PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø40',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 3,
        prixUnitaire: 15,
        tailleReference: 'PVC Ø40',
        unite: 'ml',
      },
      {
        description: 'Prise machine à laver / lave-vaisselle',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 65,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'plomb_cuisine_premium',
    nom: 'Pack Cuisine Premium',
    description: 'Standard + douchette + osmoseur + filtre',
    icone: '✨',
    prixEstime: 840,
    lignes: [
      {
        description: 'Installation évier double bac avec mitigeur douchette',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 320,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Arrivée eau chaude PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø50',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 4,
        prixUnitaire: 18,
        tailleReference: 'PVC Ø50',
        unite: 'ml',
      },
      {
        description: 'Prise lave-vaisselle',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 65,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
      {
        description: 'Installation osmoseur + robinet eau filtrée',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 195,
        tailleReference: 'PER Ø12',
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PLOMBIER_SALLE_DE_BAIN: PackTravaux[] = [
  {
    id: 'plomb_sdb_standard',
    nom: 'Pack Salle de Bain Standard',
    description: 'Lavabo + douche + WC',
    icone: '🚿',
    prixEstime: 1220,
    lignes: [
      {
        description: 'Installation lavabo avec robinetterie',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 250,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Installation douche complète avec robinetterie',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 420,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Installation WC suspendu avec bâti support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 350,
        tailleReference: 'PER Ø12',
        unite: 'forfait',
      },
      {
        description: 'Réseau eau froide PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Réseau eau chaude PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø100',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 20,
        tailleReference: 'PVC Ø100',
        unite: 'ml',
      },
    ],
  },
  {
    id: 'plomb_sdb_premium',
    nom: 'Pack Salle de Bain Premium',
    description: 'Standard + baignoire + double vasque + colonne',
    icone: '🛁',
    prixEstime: 2150,
    lignes: [
      {
        description: 'Installation double vasque avec robinetterie',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 420,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Installation baignoire avec robinetterie',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 570,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Installation colonne de douche thermostatique',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 350,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Installation WC suspendu avec bâti support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 350,
        tailleReference: 'PER Ø12',
        unite: 'forfait',
      },
      {
        description: 'Réseau eau froide PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 18,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Réseau eau chaude PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 18,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø100',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 20,
        tailleReference: 'PVC Ø100',
        unite: 'ml',
      },
    ],
  },
];

const PACKS_PLOMBIER_WC: PackTravaux[] = [
  {
    id: 'plomb_wc_standard',
    nom: 'Pack WC Standard',
    description: 'WC au sol avec arrivée et évacuation',
    icone: '🚽',
    prixEstime: 250,
    lignes: [
      {
        description: 'Installation WC au sol',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 160,
        tailleReference: 'PER Ø12',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide PER Ø12',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 4,
        prixUnitaire: 10,
        tailleReference: 'PER Ø12',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø100',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 2,
        prixUnitaire: 20,
        tailleReference: 'PVC Ø100',
        unite: 'ml',
      },
    ],
  },
];

const PACKS_PLOMBIER_BUANDERIE: PackTravaux[] = [
  {
    id: 'plomb_buanderie_standard',
    nom: 'Pack Buanderie Standard',
    description: 'Lave-linge + évier + évacuations',
    icone: '🧺',
    prixEstime: 610,
    lignes: [
      {
        description: 'Installation évier buanderie avec mitigeur',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 195,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide lave-linge',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Robinet autoperceur lave-linge',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 45,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
      {
        description: 'Évacuation PVC Ø40 lave-linge',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 4,
        prixUnitaire: 15,
        tailleReference: 'PVC Ø40',
        unite: 'ml',
      },
      {
        description: 'Siphon évier',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 35,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'plomb_buanderie_premium',
    nom: 'Pack Buanderie Premium',
    description: 'Standard + sèche-linge + bac à laver',
    icone: '✨',
    prixEstime: 850,
    lignes: [
      {
        description: 'Installation bac à laver avec robinetterie',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 250,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide lave-linge',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Arrivée eau chaude bac',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 5,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Robinet autoperceur lave-linge + sèche-linge',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 45,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
      {
        description: 'Évacuation PVC Ø50',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 18,
        tailleReference: 'PVC Ø50',
        unite: 'ml',
      },
    ],
  },
];

const PACKS_PLOMBIER_EXTERIEUR: PackTravaux[] = [
  {
    id: 'plomb_exterieur_robinet',
    nom: 'Pack Robinet Extérieur',
    description: 'Robinet de jardin + purge antigel',
    icone: '🌳',
    prixEstime: 280,
    lignes: [
      {
        description: 'Installation robinet extérieur avec purge',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 110,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
      {
        description: 'Arrivée eau froide PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 10,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Vanne d\'arrêt antigel',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 35,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'plomb_exterieur_arrosage',
    nom: 'Pack Arrosage Automatique',
    description: 'Système d\'arrosage + programmateur',
    icone: '💦',
    prixEstime: 750,
    lignes: [
      {
        description: 'Installation système d\'arrosage automatique',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 420,
        tailleReference: 'PER Ø20',
        unite: 'forfait',
      },
      {
        description: 'Réseau PER Ø20 arrosage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 25,
        prixUnitaire: 14,
        tailleReference: 'PER Ø20',
        unite: 'ml',
      },
      {
        description: 'Électrovanne + raccordements',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 85,
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_PLOMBIER_GARAGE: PackTravaux[] = [
  {
    id: 'plomb_garage_standard',
    nom: 'Pack Garage Standard',
    description: 'Lave-mains + robinet d\'arrosage',
    icone: '🚗',
    prixEstime: 380,
    lignes: [
      {
        description: 'Installation lave-mains compact',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 160,
        tailleReference: 'PER Ø12',
        unite: 'forfait',
      },
      {
        description: 'Arrivée eau froide PER Ø12',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 10,
        tailleReference: 'PER Ø12',
        unite: 'ml',
      },
      {
        description: 'Évacuation PVC Ø40',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 4,
        prixUnitaire: 15,
        tailleReference: 'PVC Ø40',
        unite: 'ml',
      },
      {
        description: 'Robinet d\'arrosage mural',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 65,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
    ],
  },
];

const PACKS_PLOMBIER_CHAUFFAGE: PackTravaux[] = [
  {
    id: 'plomb_chauffage_radiateurs',
    nom: 'Pack Radiateurs Complet',
    description: 'Installation 5 radiateurs + collecteur',
    icone: '🔥',
    prixEstime: 1650,
    lignes: [
      {
        description: 'Installation radiateur avec robinetterie thermostatique',
        type: TypeLigneDevis.PIECE,
        quantite: 5,
        prixUnitaire: 160,
        tailleReference: 'PER Ø16',
        unite: 'pièce',
      },
      {
        description: 'Réseau de distribution PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 80,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Installation collecteur de distribution',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 250,
        tailleReference: 'PER Ø20',
        unite: 'pièce',
      },
      {
        description: 'Mise en service + purge du circuit',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 150,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'plomb_chauffage_chauffe_eau',
    nom: 'Pack Chauffe-eau Électrique',
    description: 'Installation ballon 200L + groupe sécurité',
    icone: '💧',
    prixEstime: 580,
    lignes: [
      {
        description: 'Installation chauffe-eau électrique 200L',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 340,
        tailleReference: 'PER Ø16',
        unite: 'forfait',
      },
      {
        description: 'Groupe de sécurité + vase d\'expansion',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 85,
        unite: 'pièce',
      },
      {
        description: 'Raccordement eau froide/chaude PER Ø16',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 12,
        tailleReference: 'PER Ø16',
        unite: 'ml',
      },
      {
        description: 'Évacuation groupe de sécurité PVC Ø32',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 3,
        prixUnitaire: 12,
        tailleReference: 'PVC Ø32',
        unite: 'ml',
      },
    ],
  },
];

// ============================================================================
// TEMPLATES DE PACKS - CARRELEUR
// ============================================================================

const PACKS_CARRELEUR_CUISINE: PackTravaux[] = [
  {
    id: 'carrelage_cuisine_sol',
    nom: 'Pack Sol Cuisine',
    description: 'Carrelage sol + plinthes',
    icone: '🏠',
    prixEstime: 600,
    lignes: [
      {
        description: 'Pose de carrelage sol cuisine 60x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 42,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de plinthes carrelage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 15,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_cuisine_credence',
    nom: 'Pack Crédence Cuisine',
    description: 'Faïence crédence + joints',
    icone: '🎨',
    prixEstime: 340,
    lignes: [
      {
        description: 'Pose de crédence cuisine 20x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 4,
        prixUnitaire: 68,
        tailleReference: '20x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Joint silicone crédence',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_SALLE_DE_BAIN: PackTravaux[] = [
  {
    id: 'carrelage_sdb_complete',
    nom: 'Pack Salle de Bain Complète',
    description: 'Sol + murs + étanchéité',
    icone: '🛁',
    prixEstime: 1720,
    lignes: [
      {
        description: 'Pose de carrelage sol salle de bain 30x30',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 52,
        tailleReference: '30x30',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de faïence murale 25x40',
        type: TypeLigneDevis.SURFACE,
        quantite: 20,
        prixUnitaire: 62,
        tailleReference: '25x40',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Étanchéité sous carrelage (SEL)',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 24,
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Joint silicone étanchéité',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_douche_italienne',
    nom: 'Pack Douche Italienne',
    description: 'Douche complète avec étanchéité renforcée',
    icone: '🚿',
    prixEstime: 1320,
    lignes: [
      {
        description: 'Création de douche à l\'italienne',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 580,
        unite: 'forfait',
      },
      {
        description: 'Pose de carrelage sol douche 10x10 mosaïque',
        type: TypeLigneDevis.SURFACE,
        quantite: 3,
        prixUnitaire: 80,
        tailleReference: '10x10',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Pose de faïence murale douche 30x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 70,
        tailleReference: '30x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'SPEC complet douche',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 32,
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
    ],
  },
];

const PACKS_CARRELEUR_WC: PackTravaux[] = [
  {
    id: 'carrelage_wc_standard',
    nom: 'Pack WC Standard',
    description: 'Carrelage sol + habillage WC suspendu',
    icone: '🚽',
    prixEstime: 790,
    lignes: [
      {
        description: 'Pose de carrelage sol WC 30x30',
        type: TypeLigneDevis.SURFACE,
        quantite: 3,
        prixUnitaire: 55,
        tailleReference: '30x30',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Habillage carrelage WC suspendu',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 320,
        unite: 'forfait',
      },
      {
        description: 'Pose de plinthes carrelage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_wc_premium',
    nom: 'Pack WC Premium',
    description: 'Sol + mur complet + habillage WC suspendu',
    icone: '✨',
    prixEstime: 1150,
    lignes: [
      {
        description: 'Pose de carrelage sol WC 30x30',
        type: TypeLigneDevis.SURFACE,
        quantite: 3,
        prixUnitaire: 55,
        tailleReference: '30x30',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de faïence murale WC 20x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 62,
        tailleReference: '20x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Habillage carrelage WC suspendu',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 350,
        unite: 'forfait',
      },
      {
        description: 'Joint silicone étanchéité',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 6,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_TERRASSE: PackTravaux[] = [
  {
    id: 'carrelage_terrasse_standard',
    nom: 'Pack Terrasse Extérieure Standard',
    description: 'Carrelage antidérapant + joints + évacuation',
    icone: '🌿',
    prixEstime: 1720,
    lignes: [
      {
        description: 'Pose de carrelage terrasse extérieure 60x60 antidérapant',
        type: TypeLigneDevis.SURFACE,
        quantite: 25,
        prixUnitaire: 52,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Joint de dilatation périphérique',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 20,
        prixUnitaire: 10,
        unite: 'ml',
      },
      {
        description: 'Évacuation eau + pente technique',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 280,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'carrelage_balcon',
    nom: 'Pack Balcon',
    description: 'Pose + étanchéité renforcée + nez de dalle',
    icone: '🏖️',
    prixEstime: 910,
    lignes: [
      {
        description: 'Pose de carrelage balcon 30x30 antidérapant',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 62,
        tailleReference: '30x30',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Étanchéité sous carrelage (SEL)',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 30,
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Nez de dalle avec baguette aluminium',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 4,
        prixUnitaire: 25,
        unite: 'ml',
      },
      {
        description: 'Joint silicone étanchéité',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_terrasse_premium',
    nom: 'Pack Terrasse Premium Grand Format',
    description: 'Carrelage 80x80 + calepinage + plinthes',
    icone: '✨',
    prixEstime: 2280,
    lignes: [
      {
        description: 'Pose de carrelage terrasse 80x80 grand format',
        type: TypeLigneDevis.SURFACE,
        quantite: 25,
        prixUnitaire: 68,
        tailleReference: '80x80',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Plinthes carrelage extérieur',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 18,
        prixUnitaire: 12,
        unite: 'ml',
      },
      {
        description: 'Joint de dilatation + évacuation',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 320,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_CARRELEUR_SALON: PackTravaux[] = [
  {
    id: 'carrelage_salon_standard',
    nom: 'Pack Salon Standard',
    description: 'Carrelage 60x60 + plinthes',
    icone: '🛋️',
    prixEstime: 1340,
    lignes: [
      {
        description: 'Pose de carrelage sol salon 60x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 38,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de plinthes carrelage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 22,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_salon_premium',
    nom: 'Pack Salon Premium Grand Format',
    description: 'Carrelage 120x120 + pose droite/chevron',
    icone: '💎',
    prixEstime: 2720,
    lignes: [
      {
        description: 'Pose de carrelage grand format 120x120',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 72,
        tailleReference: '120x120',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Calepinage personnalisé',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 160,
        unite: 'forfait',
      },
      {
        description: 'Plinthes carrelage assorties',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 22,
        prixUnitaire: 12,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_salon_parquet',
    nom: 'Pack Salon Imitation Parquet',
    description: 'Carrelage imitation bois 20x120',
    icone: '🪵',
    prixEstime: 2000,
    lignes: [
      {
        description: 'Pose de carrelage imitation parquet 20x120',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 58,
        tailleReference: '20x120',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose en chevron ou opus',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 280,
        unite: 'forfait',
      },
      {
        description: 'Plinthes assorties',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 22,
        prixUnitaire: 10,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_ENTREE: PackTravaux[] = [
  {
    id: 'carrelage_entree_standard',
    nom: 'Pack Entrée Standard',
    description: 'Carrelage résistant + plinthes',
    icone: '🚪',
    prixEstime: 580,
    lignes: [
      {
        description: 'Pose de carrelage sol entrée 60x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 45,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de plinthes carrelage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 8,
        unite: 'ml',
      },
      {
        description: 'Protection des ouvrages pendant travaux',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 80,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'carrelage_entree_decorative',
    nom: 'Pack Entrée Décorative',
    description: 'Calepinage personnalisé + frise',
    icone: '🎨',
    prixEstime: 910,
    lignes: [
      {
        description: 'Pose de carrelage sol entrée 60x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 48,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Calepinage personnalisé avec frise décorative',
        type: TypeLigneDevis.FORFAIT,
        quantite: 1,
        prixUnitaire: 220,
        unite: 'forfait',
      },
      {
        description: 'Plinthes carrelage assorties',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 10,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_ESCALIER: PackTravaux[] = [
  {
    id: 'carrelage_escalier_interieur',
    nom: 'Pack Escalier Intérieur',
    description: 'Marches + contremarches + nez antidérapants',
    icone: '📐',
    prixEstime: 1340,
    lignes: [
      {
        description: 'Pose de carrelage marches escalier',
        type: TypeLigneDevis.PIECE,
        quantite: 12,
        prixUnitaire: 78,
        tailleReference: '30x30',
        unite: 'marche',
      },
      {
        description: 'Pose de contremarches carrelage',
        type: TypeLigneDevis.PIECE,
        quantite: 12,
        prixUnitaire: 42,
        tailleReference: '20x30',
        unite: 'contremarche',
      },
      {
        description: 'Nez de marche antidérapants',
        type: TypeLigneDevis.PIECE,
        quantite: 12,
        prixUnitaire: 18,
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'carrelage_escalier_exterieur',
    nom: 'Pack Escalier Extérieur',
    description: 'Carrelage antidérapant + bandes antidérapantes',
    icone: '🌧️',
    prixEstime: 1720,
    lignes: [
      {
        description: 'Pose de carrelage marches extérieur antidérapant',
        type: TypeLigneDevis.PIECE,
        quantite: 10,
        prixUnitaire: 88,
        tailleReference: '40x40',
        unite: 'marche',
      },
      {
        description: 'Pose de contremarches extérieur',
        type: TypeLigneDevis.PIECE,
        quantite: 10,
        prixUnitaire: 52,
        tailleReference: '30x40',
        unite: 'contremarche',
      },
      {
        description: 'Bandes antidérapantes + étanchéité',
        type: TypeLigneDevis.PIECE,
        quantite: 10,
        prixUnitaire: 28,
        unite: 'pièce',
      },
      {
        description: 'Joint silicone étanchéité',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 8,
        prixUnitaire: 10,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_GARAGE: PackTravaux[] = [
  {
    id: 'carrelage_garage_standard',
    nom: 'Pack Sol Garage',
    description: 'Carrelage résistant grand format + joints industriels',
    icone: '🚗',
    prixEstime: 1560,
    lignes: [
      {
        description: 'Pose de carrelage sol garage 60x60 renforcé',
        type: TypeLigneDevis.SURFACE,
        quantite: 35,
        prixUnitaire: 42,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Joint époxy industriel',
        type: TypeLigneDevis.SURFACE,
        quantite: 35,
        prixUnitaire: 6,
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
    ],
  },
  {
    id: 'carrelage_garage_premium',
    nom: 'Pack Garage Premium',
    description: 'Grand format 80x80 + joints renforcés',
    icone: '🏎️',
    prixEstime: 2180,
    lignes: [
      {
        description: 'Pose de carrelage garage 80x80 haute résistance',
        type: TypeLigneDevis.SURFACE,
        quantite: 35,
        prixUnitaire: 54,
        tailleReference: '80x80',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Joint époxy industriel renforcé',
        type: TypeLigneDevis.SURFACE,
        quantite: 35,
        prixUnitaire: 8,
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Plinthe périphérique',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 25,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
];

const PACKS_CARRELEUR_CHAMBRE: PackTravaux[] = [
  {
    id: 'carrelage_chambre_standard',
    nom: 'Pack Chambre Standard',
    description: 'Carrelage 60x60 + plinthes',
    icone: '🛏️',
    prixEstime: 850,
    lignes: [
      {
        description: 'Pose de carrelage sol chambre 60x60',
        type: TypeLigneDevis.SURFACE,
        quantite: 18,
        prixUnitaire: 42,
        tailleReference: '60x60',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Pose de plinthes carrelage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 16,
        prixUnitaire: 8,
        unite: 'ml',
      },
    ],
  },
  {
    id: 'carrelage_chambre_parquet',
    nom: 'Pack Chambre Imitation Parquet',
    description: 'Carrelage imitation bois 20x120',
    icone: '🪵',
    prixEstime: 1280,
    lignes: [
      {
        description: 'Pose de carrelage imitation parquet 20x120',
        type: TypeLigneDevis.SURFACE,
        quantite: 18,
        prixUnitaire: 62,
        tailleReference: '20x120',
        unite: 'm²',
        modeSaisieSurface: 'direct',
        avecPertes: true,
      },
      {
        description: 'Plinthes assorties',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 16,
        prixUnitaire: 10,
        unite: 'ml',
      },
    ],
  },
];

// ============================================================================
// TEMPLATES DE PACKS - PEINTRE
// ============================================================================

const PACKS_PEINTRE_CHAMBRE: PackTravaux[] = [
  {
    id: 'peinture_chambre_standard',
    nom: 'Pack Chambre Standard',
    description: 'Murs et plafond - Peinture acrylique mate',
    icone: '🎨',
    prixEstime: 420,
    lignes: [
      {
        description: 'Peinture murs acrylique mate (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 40,
        prixUnitaire: 8,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 15,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 80,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_chambre_premium',
    nom: 'Pack Chambre Premium',
    description: 'Murs satinés + plafond + boiseries',
    icone: '✨',
    prixEstime: 680,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 40,
        prixUnitaire: 12,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 15,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture porte intérieure (2 faces)',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 95,
        tailleReference: 'Glycéro satinée',
        unite: 'pièce',
      },
      {
        description: 'Peinture plinthes et encadrements',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 16,
        prixUnitaire: 5,
        tailleReference: 'Glycéro satinée',
        unite: 'ml',
      },
      {
        description: 'Ponçage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 120,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_SALON: PackTravaux[] = [
  {
    id: 'peinture_salon_standard',
    nom: 'Pack Salon Standard',
    description: 'Murs et plafond - Peinture acrylique mate',
    icone: '🎨',
    prixEstime: 720,
    lignes: [
      {
        description: 'Peinture murs acrylique mate (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 70,
        prixUnitaire: 8,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 150,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_salon_premium',
    nom: 'Pack Salon Premium',
    description: 'Murs satinés + plafond + effet décoratif',
    icone: '✨',
    prixEstime: 1250,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 70,
        prixUnitaire: 12,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Mur accent effet décoratif (béton ciré ou stucco)',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 25,
        tailleReference: 'Enduit décoratif',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plinthes et encadrements',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 25,
        prixUnitaire: 5,
        tailleReference: 'Glycéro satinée',
        unite: 'ml',
      },
      {
        description: 'Ponçage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 200,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_CUISINE: PackTravaux[] = [
  {
    id: 'peinture_cuisine_standard',
    nom: 'Pack Cuisine Standard',
    description: 'Murs et plafond - Peinture lessivable',
    icone: '🎨',
    prixEstime: 380,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 10,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat anti-humidité',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 9,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 70,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_cuisine_premium',
    nom: 'Pack Cuisine Premium',
    description: 'Murs lessivables + plafond anti-humidité + boiseries',
    icone: '✨',
    prixEstime: 580,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 12,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat anti-humidité',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 9,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plinthes et encadrements',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 15,
        prixUnitaire: 5,
        tailleReference: 'Glycéro satinée',
        unite: 'ml',
      },
      {
        description: 'Ponçage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 100,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_SALLE_DE_BAIN: PackTravaux[] = [
  {
    id: 'peinture_sdb_standard',
    nom: 'Pack SDB Standard',
    description: 'Peinture anti-humidité murs et plafond',
    icone: '🎨',
    prixEstime: 320,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée anti-humidité (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 22,
        prixUnitaire: 11,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat anti-humidité',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 10,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et traitement anti-moisissure',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 60,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_sdb_premium',
    nom: 'Pack SDB Premium',
    description: 'Peinture anti-humidité + glycéro boiseries',
    icone: '✨',
    prixEstime: 480,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée anti-humidité (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 22,
        prixUnitaire: 12,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat anti-humidité',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 10,
        tailleReference: 'Acrylique anti-humidité',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture porte intérieure (2 faces)',
        type: TypeLigneDevis.PIECE,
        quantite: 1,
        prixUnitaire: 95,
        tailleReference: 'Glycéro satinée',
        unite: 'pièce',
      },
      {
        description: 'Peinture plinthes et encadrements glycéro',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 12,
        prixUnitaire: 6,
        tailleReference: 'Glycéro satinée',
        unite: 'ml',
      },
      {
        description: 'Traitement anti-moisissure et préparation',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 85,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_WC: PackTravaux[] = [
  {
    id: 'peinture_wc_standard',
    nom: 'Pack WC Standard',
    description: 'Murs et plafond - Peinture lessivable',
    icone: '🎨',
    prixEstime: 180,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 10,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 3,
        prixUnitaire: 8,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 40,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_BUREAU: PackTravaux[] = [
  {
    id: 'peinture_bureau_standard',
    nom: 'Pack Bureau Standard',
    description: 'Murs et plafond - Peinture mate',
    icone: '🎨',
    prixEstime: 450,
    lignes: [
      {
        description: 'Peinture murs acrylique mate (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 45,
        prixUnitaire: 8,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 15,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 90,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_ENTREE: PackTravaux[] = [
  {
    id: 'peinture_entree_standard',
    nom: 'Pack Entrée Standard',
    description: 'Murs et plafond - Peinture lessivable',
    icone: '🎨',
    prixEstime: 280,
    lignes: [
      {
        description: 'Peinture murs acrylique satinée lessivable (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 20,
        prixUnitaire: 10,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond blanc mat',
        type: TypeLigneDevis.SURFACE,
        quantite: 8,
        prixUnitaire: 7,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Lessivage et préparation support',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 60,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_BOISERIE: PackTravaux[] = [
  {
    id: 'peinture_boiserie_portes',
    nom: 'Pack Portes Intérieures',
    description: 'Peinture glycéro 3 portes (2 faces)',
    icone: '🚪',
    prixEstime: 285,
    lignes: [
      {
        description: 'Peinture porte intérieure glycéro (2 faces)',
        type: TypeLigneDevis.PIECE,
        quantite: 3,
        prixUnitaire: 95,
        tailleReference: 'Glycéro satinée',
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'peinture_boiserie_fenetres',
    nom: 'Pack Fenêtres',
    description: 'Peinture glycéro fenêtres intérieures',
    icone: '🪟',
    prixEstime: 380,
    lignes: [
      {
        description: 'Peinture fenêtre intérieure glycéro',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 85,
        tailleReference: 'Glycéro satinée',
        unite: 'pièce',
      },
      {
        description: 'Décapage et ponçage',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 40,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_boiserie_plinthes',
    nom: 'Pack Plinthes',
    description: 'Peinture plinthes glycéro',
    icone: '📏',
    prixEstime: 320,
    lignes: [
      {
        description: 'Peinture plinthes glycéro satinée',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 50,
        prixUnitaire: 5,
        tailleReference: 'Glycéro satinée',
        unite: 'ml',
      },
      {
        description: 'Ponçage et préparation',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 70,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_EXTERIEUR: PackTravaux[] = [
  {
    id: 'peinture_ext_volets',
    nom: 'Pack Volets Bois',
    description: 'Peinture 4 volets bois extérieur',
    icone: '🪟',
    prixEstime: 520,
    lignes: [
      {
        description: 'Peinture volet bois extérieur (2 faces)',
        type: TypeLigneDevis.PIECE,
        quantite: 4,
        prixUnitaire: 110,
        tailleReference: 'Glycéro brillante',
        unite: 'pièce',
      },
      {
        description: 'Décapage et ponçage volets',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 80,
        unite: 'forfait',
      },
    ],
  },
  {
    id: 'peinture_ext_portail',
    nom: 'Pack Portail Métallique',
    description: 'Peinture portail avec traitement antirouille',
    icone: '🚧',
    prixEstime: 680,
    lignes: [
      {
        description: 'Peinture portail métallique avec antirouille',
        type: TypeLigneDevis.SURFACE,
        quantite: 15,
        prixUnitaire: 18,
        tailleReference: 'Glycéro brillante',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Décapage et traitement antirouille',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 150,
        unite: 'forfait',
      },
      {
        description: 'Peinture piliers portail',
        type: TypeLigneDevis.PIECE,
        quantite: 2,
        prixUnitaire: 65,
        tailleReference: 'Acrylique extérieure',
        unite: 'pièce',
      },
    ],
  },
  {
    id: 'peinture_ext_facade',
    nom: 'Pack Façade Petit Côté',
    description: 'Peinture façade 30m² avec sous-couche',
    icone: '🏠',
    prixEstime: 950,
    lignes: [
      {
        description: 'Sous-couche fixateur façade',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 6,
        tailleReference: 'Fixateur façade',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture façade acrylique (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 30,
        prixUnitaire: 15,
        tailleReference: 'Acrylique façade',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Nettoyage façade haute pression',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 180,
        unite: 'forfait',
      },
      {
        description: 'Rebouchage fissures et préparation',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 220,
        unite: 'forfait',
      },
    ],
  },
];

const PACKS_PEINTRE_CAGE_ESCALIER: PackTravaux[] = [
  {
    id: 'peinture_cage_escalier',
    nom: 'Pack Cage d\'Escalier',
    description: 'Peinture cage escalier avec hauteur sous plafond',
    icone: '🪜',
    prixEstime: 780,
    lignes: [
      {
        description: 'Peinture murs cage escalier (2 couches)',
        type: TypeLigneDevis.SURFACE,
        quantite: 45,
        prixUnitaire: 12,
        tailleReference: 'Acrylique satinée',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Peinture plafond cage escalier',
        type: TypeLigneDevis.SURFACE,
        quantite: 12,
        prixUnitaire: 10,
        tailleReference: 'Acrylique mate',
        unite: 'm²',
        modeSaisieSurface: 'direct',
      },
      {
        description: 'Location échafaudage/nacelle',
        type: TypeLigneDevis.UNITAIRE,
        quantite: 1,
        prixUnitaire: 200,
        unite: 'forfait',
      },
    ],
  },
];

// ============================================================================
// DÉFINITION DES PIÈCES
// ============================================================================

export const PIECES_TEMPLATES: PieceTemplate[] = [
  {
    id: 'cuisine',
    nom: 'Cuisine',
    icone: '🍳',
    description: 'Packs de travaux pour cuisine',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PLOMBIER, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_CUISINE,
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_CUISINE,
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_CUISINE,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_CUISINE,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'salle_de_bain',
    nom: 'Salle de Bain',
    icone: '🛁',
    description: 'Packs de travaux pour salle de bain',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PLOMBIER, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_SALLE_DE_BAIN,
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_SALLE_DE_BAIN,
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_SALLE_DE_BAIN,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_SALLE_DE_BAIN,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'wc',
    nom: 'WC',
    icone: '🚽',
    description: 'Packs de travaux pour WC',
    metiers: [CorpsMetier.PLOMBIER, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_WC,
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_WC,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_WC,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'salon',
    nom: 'Salon / Séjour',
    icone: '🛋️',
    description: 'Packs de travaux pour salon et séjour',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_SALON,
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_SALON,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_SALON,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'chambre',
    nom: 'Chambre',
    icone: '🛏️',
    description: 'Packs de travaux pour chambre',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_CHAMBRE,
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_CHAMBRE,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_CHAMBRE,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'terrasse',
    nom: 'Terrasse / Balcon',
    icone: '🌿',
    description: 'Packs de travaux pour terrasse et balcon',
    metiers: [CorpsMetier.CARRELEUR, CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_TERRASSE,
      [CorpsMetier.MACON]: PACKS_MACON_TERRASSE,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'entree',
    nom: 'Entrée / Hall',
    icone: '🚪',
    description: 'Packs de travaux pour entrée et hall',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.CARRELEUR, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_ENTREE,
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_ENTREE,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_ENTREE,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'escalier',
    nom: 'Escalier',
    icone: '📐',
    description: 'Packs de travaux pour escalier intérieur et extérieur',
    metiers: [CorpsMetier.CARRELEUR],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_ESCALIER,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'garage',
    nom: 'Garage / Atelier',
    icone: '🚗',
    description: 'Packs de travaux pour garage et atelier',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PLOMBIER, CorpsMetier.CARRELEUR],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_GARAGE,
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_GARAGE,
      [CorpsMetier.CARRELEUR]: PACKS_CARRELEUR_GARAGE,
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'bureau',
    nom: 'Bureau',
    icone: '💼',
    description: 'Packs de travaux pour bureau',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_BUREAU,
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_BUREAU,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'buanderie',
    nom: 'Buanderie / Cellier',
    icone: '🧺',
    description: 'Packs de travaux pour buanderie et cellier',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PLOMBIER],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_BUANDERIE,
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_BUANDERIE,
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'exterieur',
    nom: 'Extérieur / Jardin',
    icone: '🌳',
    description: 'Packs de travaux pour extérieur et jardin',
    metiers: [CorpsMetier.ELECTRICIEN, CorpsMetier.PLOMBIER, CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_EXTERIEUR,
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_EXTERIEUR,
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_EXTERIEUR,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'cave',
    nom: 'Cave / Sous-sol',
    icone: '🍷',
    description: 'Packs de travaux pour cave et sous-sol',
    metiers: [CorpsMetier.ELECTRICIEN],
    packs: {
      [CorpsMetier.ELECTRICIEN]: PACKS_ELECTRICIEN_CAVE,
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'chauffage',
    nom: 'Chauffage / Eau chaude',
    icone: '🔥',
    description: 'Packs de travaux pour chauffage et eau chaude',
    metiers: [CorpsMetier.PLOMBIER],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: PACKS_PLOMBIER_CHAUFFAGE,
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  // ============================================================================
  // PIÈCES SPÉCIFIQUES MAÇON
  // ============================================================================
  {
    id: 'extension_agrandissement',
    nom: 'Extension / Agrandissement',
    icone: '🏗️',
    description: 'Packs pour extension et agrandissement',
    metiers: [CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: PACKS_MACON_EXTENSION,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'murs_cloisons',
    nom: 'Murs et Cloisons',
    icone: '🧱',
    description: 'Packs pour création et modification de murs',
    metiers: [CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: PACKS_MACON_MURS,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'escaliers',
    nom: 'Escaliers',
    icone: '🪜',
    description: 'Packs pour escaliers intérieurs et extérieurs',
    metiers: [CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: PACKS_MACON_ESCALIERS,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'renovation_demo',
    nom: 'Rénovation / Démolition',
    icone: '💥',
    description: 'Packs pour rénovation et travaux de démolition',
    metiers: [CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: PACKS_MACON_RENOVATION,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'etancheite_drainage',
    nom: 'Étanchéité / Drainage',
    icone: '💧',
    description: 'Packs pour traitement humidité et drainage',
    metiers: [CorpsMetier.MACON],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: PACKS_MACON_ETANCHEITE,
      [CorpsMetier.PEINTRE]: [],
      [CorpsMetier.GENERAL]: [],
    },
  },
  // ============================================================================
  // PIÈCES SPÉCIFIQUES PEINTRE
  // ============================================================================
  {
    id: 'boiserie',
    nom: 'Boiseries',
    icone: '🚪',
    description: 'Packs pour peinture des portes, fenêtres et plinthes',
    metiers: [CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_BOISERIE,
      [CorpsMetier.GENERAL]: [],
    },
  },
  {
    id: 'cage_escalier',
    nom: 'Cage d\'Escalier',
    icone: '🪜',
    description: 'Packs pour peinture de cage d\'escalier',
    metiers: [CorpsMetier.PEINTRE],
    packs: {
      [CorpsMetier.ELECTRICIEN]: [],
      [CorpsMetier.PLOMBIER]: [],
      [CorpsMetier.CARRELEUR]: [],
      [CorpsMetier.MACON]: [],
      [CorpsMetier.PEINTRE]: PACKS_PEINTRE_CAGE_ESCALIER,
      [CorpsMetier.GENERAL]: [],
    },
  },
];

// Helper pour obtenir les pièces compatibles avec un métier
export const getPiecesForMetier = (metier: CorpsMetier): PieceTemplate[] => {
  return PIECES_TEMPLATES.filter(piece => piece.metiers.includes(metier));
};

// Helper pour obtenir les packs d'une pièce pour un métier
export const getPacksForPieceAndMetier = (pieceId: string, metier: CorpsMetier): PackTravaux[] => {
  const piece = PIECES_TEMPLATES.find(p => p.id === pieceId);
  if (!piece) return [];
  return piece.packs[metier] || [];
};
